import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import * as React from "react";

import { useAuth } from "@/providers";
import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { useSkills } from "@/shared/domain/skills/hooks";
import { parseSlugs, resolveSlugs } from "@/shared/domain/skills/slugParse";

import styles from "./UserMessage.module.css";

interface UserMessageProps {
  /**
   * Text content to display
   */
  text: string;
  isFailed?: boolean;
  onRetry?: () => void;
}

/**
 * Component for rendering user messages.
 *
 * Splits the text into plain runs and ``/skill-name[@vN]`` slug spans so the
 * chat history visually distinguishes invoked skills from prose. The slug
 * text is rendered verbatim — the embedded ``@vN`` (if any) is preserved as
 * the chat-history record of which version was invoked.
 *
 * Slug-shaped tokens that don't resolve against the viewer's Personal-tab
 * skills (the same source the composer uses to authorize invocation) render
 * as plain text. Without that filter, any pattern-matching token like
 * ``/notaskill`` or ``/path/to/file`` would falsely advertise itself as an
 * invocation.
 */
export function UserMessage({
  text,
  isFailed = false,
  onRetry,
}: UserMessageProps) {
  return (
    <div className={styles.indentedContainer}>
      <div
        className={`${styles.messageContainer} ${isFailed ? styles.failedMessageContainer : ""}`.trim()}
      >
        <RobotoTypography className={styles.text}>
          <MessageContent text={text} />
        </RobotoTypography>
        {isFailed && (
          <div className={styles.failedActions}>
            <RobotoTypography className={styles.failedText}>
              Failed to send
            </RobotoTypography>
            {onRetry && (
              <button
                type="button"
                className={styles.retryButton}
                onClick={onRetry}
              >
                Retry
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function MessageContent({ text }: { text: string }) {
  const { currentOrganization } = useAuth();
  const skillsQuery = useSkills(currentOrganization?.org_id, "personal");

  // Only chip-render slugs that resolve to a real skill the viewer can see.
  // Mirrors the composer's Personal-tab source — see ChatInputBox.tsx — so the
  // rendered chips match what the user could have invoked. While the query is
  // in flight (or for viewers whose Personal-tab doesn't contain the skill),
  // unresolved tokens fall through to plain text.
  const resolvedSlugs = React.useMemo(() => {
    const skills = skillsQuery.data?.items ?? [];
    return resolveSlugs(parseSlugs(text), skills).filter(
      (slug) => slug.skill !== null,
    );
  }, [skillsQuery.data, text]);

  if (resolvedSlugs.length === 0) {
    return <>{text}</>;
  }
  const segments: React.ReactNode[] = [];
  let cursor = 0;
  resolvedSlugs.forEach((slug, idx) => {
    if (slug.start > cursor) {
      segments.push(text.slice(cursor, slug.start));
    }
    segments.push(
      <span key={`slug-${idx}-${slug.start}`} className={styles.slug}>
        <AutoAwesomeIcon className={styles.slugIcon} fontSize="inherit" />
        {text.slice(slug.start, slug.end)}
      </span>,
    );
    cursor = slug.end;
  });
  if (cursor < text.length) {
    segments.push(text.slice(cursor));
  }
  return <>{segments}</>;
}
